import {
  assertNever,
  CurrencyIcon,
  Divider,
  Flex,
  InlineFormattedNumber,
  Stack,
  Text,
  type Currency,
  type Security,
} from '@talos/kyoko';
import {
  getProductTemplateTooltipModel,
  ProductTemplateTooltipRowType,
  type ProductTemplateTooltipRow,
} from './getProductTemplateTooltipModel';

function ProductTemplateTooltipRowValue({ row }: { row: ProductTemplateTooltipRow }) {
  switch (row.type) {
    case ProductTemplateTooltipRowType.Text: {
      const isDefaultEmphasis = row.emphasis === 'default';
      return (
        <Text
          color={isDefaultEmphasis ? 'colorTextDefault' : 'colorTextImportant'}
          size="fontSizeSmall"
          weight={isDefaultEmphasis ? undefined : 'fontWeightMedium'}
        >
          {row.value}
        </Text>
      );
    }
    case ProductTemplateTooltipRowType.Asset:
      return (
        <Flex alignItems="center" gap="spacing4">
          <CurrencyIcon currency={row.value} size={12} />
          <Text color="colorTextImportant" size="fontSizeSmall" weight="fontWeightMedium">
            {row.value}
          </Text>
        </Flex>
      );
    case ProductTemplateTooltipRowType.Number:
      return (
        <InlineFormattedNumber
          number={row.value}
          currency={row.currency}
          increment={row.increment}
          align="right"
          highlightColor="colorTextImportant"
        />
      );
    default:
      return assertNever(row);
  }
}

function ProductTemplateTooltipSection({ rows }: { rows: ProductTemplateTooltipRow[] }) {
  return (
    <Stack alignItems="stretch" gap="spacing8">
      <Divider orientation="horizontal" />
      {rows.map((row, rowIndex) => (
        <Flex key={`${rowIndex}-${row.label}`} justifyContent="space-between" gap="spacing8">
          <Text color="colorTextDefault" size="fontSizeSmall">
            {row.label}
          </Text>
          <ProductTemplateTooltipRowValue row={row} />
        </Flex>
      ))}
    </Stack>
  );
}

export function ProductTemplateTooltip({
  security,
  currenciesBySymbol,
}: {
  security: Security;
  currenciesBySymbol?: Map<string, Currency>;
}) {
  const model = getProductTemplateTooltipModel(security, currenciesBySymbol);

  return (
    <Stack data-testid="product-template-tooltip" alignItems="stretch" gap="spacing8" minWidth="220px">
      <Flex justifyContent="space-between" alignItems="flex-start" gap="spacing8" w="100%">
        <Text
          color="colorTextImportant"
          size="fontSizeSmall"
          weight="fontWeightMedium"
          flex={1}
          minWidth="0px"
          whiteSpace="normal"
          data-testid="product-template-tooltip-name"
        >
          {model.name}
        </Text>
        <Text
          color="colorTextDefault"
          size="fontSizeSmall"
          display="block"
          textAlign="right"
          whiteSpace="nowrap"
          flexShrink={0}
          data-testid="product-template-tooltip-product-type"
        >
          {model.productType}
        </Text>
      </Flex>
      {model.sections.map(section => (
        <ProductTemplateTooltipSection key={section.map(row => row.label).join('|')} rows={section} />
      ))}
    </Stack>
  );
}
